import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, AlertTriangle, ChevronDown, ChevronUp, Info, Zap, RefreshCw } from "lucide-react";

export function SignalCard({ sig, title, type, multiIndicatorData, lang }) {
  if (!sig) return (
    <Card className="bg-zinc-900 border-zinc-700 border-2">
      <CardContent className="p-4 text-center">
        <p className="text-zinc-500">Loading signal...</p>
      </CardContent>
    </Card>
  );

  const isBuy = sig.signal_type === "BUY";
  const confidence = sig.confidence || 0;

  const getConfidenceStyle = (conf) => {
    if (conf >= 70) return { border: "border-green-500", badge: "bg-green-600", text: "text-green-400", label: "Strong Signal", warning: null };
    if (conf >= 50) return { border: "border-yellow-500", badge: "bg-yellow-600", text: "text-yellow-400", label: "Moderate Signal", warning: "Wait for more confirmation" };
    if (conf >= 30) return { border: "border-orange-500", badge: "bg-orange-600", text: "text-orange-400", label: "Weak Signal", warning: "High risk - Use tight stop loss" };
    return { border: "border-red-500", badge: "bg-red-600", text: "text-red-400", label: "Very Weak", warning: "Not recommended - Wait for better setup" };
  };

  const style = getConfidenceStyle(confidence);
  const signalBg = isBuy ? "bg-green-600" : "bg-red-600";
  const reasons = sig.reasons || [];
  const headlines = multiIndicatorData?.market_context?.headlines || [];
  const economicEvents = multiIndicatorData?.market_context?.economic_events || [];

  return (
    <Card className={`border-2 ${style.border} bg-zinc-900`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2 text-white">
            {isBuy ? <TrendingUp className="w-5 h-5 text-green-400" /> : <TrendingDown className="w-5 h-5 text-red-400" />}
            {title}
          </CardTitle>
          <Badge className={`text-lg px-3 py-1 ${signalBg}`}>{sig.signal_type}</Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-center mb-4">
          <p className={`text-5xl font-bold ${style.text}`}>{confidence}%</p>
          <p className="text-zinc-300 text-sm">Confidence</p>
          <Badge className={`mt-2 ${style.badge}`}>{style.label}</Badge>
          {style.warning && (
            <p className="text-xs text-orange-400 mt-2 flex items-center justify-center gap-1">
              <AlertTriangle className="w-3 h-3" />{style.warning}
            </p>
          )}
        </div>

        <div className="space-y-3">
          {sig.entry_aggressive && sig.entry_conservative ? (
            <>
              {/* Aggressive Entry */}
              <div className="border-2 border-amber-500/40 rounded-lg p-3 bg-amber-500/5" data-testid="entry-aggressive-box">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-amber-400 text-xs font-bold">{lang === "ar" ? "دخول جريء (0.3×ATR)" : "Aggressive Entry (0.3×ATR)"}</span>
                  <Badge variant="outline" className="text-[10px] border-amber-500 text-amber-400">{lang === "ar" ? "مبكر" : "Early"}</Badge>
                </div>
                <div className="grid grid-cols-4 gap-2 text-xs">
                  <div><span className="text-zinc-500 block">Entry</span><span className="font-mono text-white">{sig.entry_aggressive.price}</span></div>
                  <div><span className="text-zinc-500 block">SL</span><span className="font-mono text-red-400">{sig.entry_aggressive.stop_loss}</span></div>
                  <div><span className="text-zinc-500 block">TP1</span><span className="font-mono text-green-400">{sig.entry_aggressive.take_profit_1}</span></div>
                  <div><span className="text-zinc-500 block">TP2</span><span className="font-mono text-green-400">{sig.entry_aggressive.take_profit_2}</span></div>
                </div>
              </div>
              {/* Conservative Entry */}
              <div className="border-2 border-cyan-500/40 rounded-lg p-3 bg-cyan-500/5" data-testid="entry-conservative-box">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-cyan-400 text-xs font-bold">{lang === "ar" ? "دخول متحفظ (0.7×ATR)" : "Conservative Entry (0.7×ATR)"}</span>
                  <Badge variant="outline" className="text-[10px] border-cyan-500 text-cyan-400">{lang === "ar" ? "آمن" : "Safer"}</Badge>
                </div>
                <div className="grid grid-cols-4 gap-2 text-xs">
                  <div><span className="text-zinc-500 block">Entry</span><span className="font-mono text-white">{sig.entry_conservative.price}</span></div>
                  <div><span className="text-zinc-500 block">SL</span><span className="font-mono text-red-400">{sig.entry_conservative.stop_loss}</span></div>
                  <div><span className="text-zinc-500 block">TP1</span><span className="font-mono text-green-400">{sig.entry_conservative.take_profit_1}</span></div>
                  <div><span className="text-zinc-500 block">TP2</span><span className="font-mono text-green-400">{sig.entry_conservative.take_profit_2}</span></div>
                </div>
              </div>
              {sig.current_price && (
                <p className="text-[10px] text-zinc-500 text-center">
                  {lang === "ar" ? "السعر الحالي" : "Current Price"}: <span className="font-mono text-zinc-300">{sig.current_price}</span>
                  {sig.atr_value && <> · ATR: <span className="font-mono text-zinc-300">{sig.atr_value}</span></>}
                </p>
              )}
            </>
          ) : (
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-zinc-800 p-3 rounded border border-zinc-700">
                <span className="text-zinc-400 text-xs block mb-1">Entry Price</span>
                <span className="font-mono text-xl text-white">{sig.entry_price}</span>
              </div>
              <div className="bg-zinc-800 p-3 rounded border border-zinc-700">
                <span className="text-zinc-400 text-xs block mb-1">Stop Loss</span>
                <span className="font-mono text-xl text-red-400">{sig.stop_loss}</span>
              </div>
              <div className="bg-zinc-800 p-3 rounded border border-zinc-700">
                <span className="text-zinc-400 text-xs block mb-1">Take Profit 1</span>
                <span className="font-mono text-xl text-green-400">{sig.take_profit_1}</span>
              </div>
              <div className="bg-zinc-800 p-3 rounded border border-zinc-700">
                <span className="text-zinc-400 text-xs block mb-1">Take Profit 2</span>
                <span className="font-mono text-xl text-green-400">{sig.take_profit_2}</span>
              </div>
            </div>
          )}
        </div>

        <div className="mt-3">
          <Badge variant="outline" className="text-xs text-white border-zinc-600">{sig.timeframe}</Badge>
          <span className="text-sm text-zinc-300 ml-2">{sig.symbol}</span>
        </div>

        <div className="mt-3 pt-3 border-t border-zinc-700">
          <p className="text-xs text-blue-400 font-semibold mb-1">Technical:</p>
          <p className="text-xs text-zinc-200">{reasons[0] || "Analyzing..."}</p>
          {reasons[1] && <p className="text-xs text-zinc-200">{reasons[1]}</p>}
        </div>

        {headlines.length > 0 && (
          <div className="mt-2 pt-2 border-t border-zinc-700">
            <p className="text-xs text-yellow-400 font-semibold mb-1">{lang === "ar" ? "اخبار مباشرة" : "Live News"}:</p>
            {headlines.slice(0, 2).map((h, i) => (
              <p key={i} className="text-xs text-zinc-300 line-clamp-1">{h}</p>
            ))}
          </div>
        )}

        {economicEvents.length > 0 && (
          <div className="mt-2 pt-2 border-t border-zinc-700">
            <p className="text-xs text-purple-400 font-semibold mb-1">{lang === "ar" ? "احداث اقتصادية" : "Economic Events"}:</p>
            {economicEvents.slice(0, 2).map((e, i) => (
              <p key={i} className="text-xs text-zinc-300">{e}</p>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function EducationCard({ title, icon, description, howItWorks, signals, color }) {
  const [expanded, setExpanded] = useState(false);
  const colorMap = {
    purple: "border-purple-500 bg-purple-500/10",
    green: "border-green-500 bg-green-500/10",
    blue: "border-blue-500 bg-blue-500/10",
    yellow: "border-yellow-500 bg-yellow-500/10",
    orange: "border-orange-500 bg-orange-500/10",
    cyan: "border-cyan-500 bg-cyan-500/10",
  };
  const borderClass = colorMap[color] || colorMap.blue;

  return (
    <div className={`border-l-4 ${borderClass} rounded-lg p-4`}>
      <button className="w-full flex items-center justify-between text-left" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-3">
          <span className="text-2xl">{icon}</span>
          <div>
            <h3 className="font-bold text-white text-lg">{title}</h3>
            <p className="text-zinc-400 text-sm">{description}</p>
          </div>
        </div>
        {expanded ? <ChevronUp className="w-5 h-5 text-zinc-400" /> : <ChevronDown className="w-5 h-5 text-zinc-400" />}
      </button>
      {expanded && (
        <div className="mt-4 space-y-4 animate-in slide-in-from-top-2">
          <div>
            <h4 className="text-sm font-semibold text-zinc-300 mb-2 flex items-center gap-2"><Info className="w-4 h-4" /> How It Works</h4>
            <ul className="space-y-1">
              {howItWorks.map((item, i) => (
                <li key={i} className="text-sm text-zinc-400 flex items-start gap-2">
                  <span className="text-blue-400 mt-1">&#8226;</span>{item}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-zinc-300 mb-2 flex items-center gap-2"><Zap className="w-4 h-4" /> Trading Signals</h4>
            <div className="space-y-2">
              {signals.map((sig, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <Badge className={sig.type === "BUY" ? "bg-green-600" : sig.type === "SELL" ? "bg-red-600" : "bg-blue-600"}>{sig.type}</Badge>
                  <span className="text-zinc-300">{sig.condition}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function MethodSignalView({ data, method, timeframe, loading, symbol, lang }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-48 mb-6">
        <RefreshCw className="w-8 h-8 animate-spin text-purple-500" />
        <span className="ml-3 text-zinc-400">Loading {method} signal...</span>
      </div>
    );
  }

  if (!data || !data.indicators) {
    return (
      <div className="text-center text-zinc-500 py-8 mb-6">
        <p>No signal data available. Please wait...</p>
      </div>
    );
  }

  const indicatorData = data.indicators[method];
  if (!indicatorData) {
    return (
      <div className="text-center text-zinc-500 py-8 mb-6">
        <p>No data for {method} indicator</p>
      </div>
    );
  }

  const isBuy = indicatorData.signal_type === "BUY";
  const isSell = indicatorData.signal_type === "SELL";
  const isSR = method === "support_resistance";

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
      <Card className={`border-2 ${isBuy ? "border-green-500 bg-green-950/30" : "border-zinc-700 bg-zinc-900 opacity-60"}`}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingUp className={`w-6 h-6 ${isBuy ? "text-green-400" : "text-zinc-500"}`} />
              <span className={`text-xl font-bold ${isBuy ? "text-green-400" : "text-zinc-500"}`}>BUY</span>
            </div>
            {isBuy && <Badge className="bg-green-600">{indicatorData.strength}</Badge>}
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-zinc-300 mb-3">{lang === "ar" ? indicatorData.description_ar : indicatorData.description}</p>
          {indicatorData.value !== null && <p className="text-zinc-400 text-sm">Value: <span className="font-mono text-white">{indicatorData.value}</span></p>}
          <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
            <div className="bg-zinc-800 p-2 rounded"><span className="text-zinc-500 block">Entry</span><span className="font-mono text-white">{indicatorData.entry_price}</span></div>
            <div className="bg-zinc-800 p-2 rounded"><span className="text-zinc-500 block">SL</span><span className="font-mono text-red-400">{indicatorData.stop_loss}</span></div>
            <div className="bg-zinc-800 p-2 rounded"><span className="text-zinc-500 block">TP</span><span className="font-mono text-green-400">{indicatorData.take_profit}</span></div>
          </div>
          {isSR && indicatorData.nearest_support && (
            <div className="mt-2 text-xs">
              <span className="text-zinc-400">Support: </span><span className="font-mono text-green-400">{indicatorData.nearest_support}</span>
              <span className="text-zinc-400 ml-3">Resistance: </span><span className="font-mono text-red-400">{indicatorData.nearest_resistance}</span>
            </div>
          )}
          {indicatorData.risk_warning && (
            <p className="text-xs text-orange-400 mt-2 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{lang === "ar" ? indicatorData.risk_warning_ar : indicatorData.risk_warning}</p>
          )}
        </CardContent>
      </Card>

      <Card className={`border-2 ${isSell ? "border-red-500 bg-red-950/30" : "border-zinc-700 bg-zinc-900 opacity-60"}`}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingDown className={`w-6 h-6 ${isSell ? "text-red-400" : "text-zinc-500"}`} />
              <span className={`text-xl font-bold ${isSell ? "text-red-400" : "text-zinc-500"}`}>SELL</span>
            </div>
            {isSell && <Badge className="bg-red-600">{indicatorData.strength}</Badge>}
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-zinc-300 mb-3">{lang === "ar" ? indicatorData.description_ar : indicatorData.description}</p>
          {indicatorData.value !== null && <p className="text-zinc-400 text-sm">Value: <span className="font-mono text-white">{indicatorData.value}</span></p>}
          <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
            <div className="bg-zinc-800 p-2 rounded"><span className="text-zinc-500 block">Entry</span><span className="font-mono text-white">{indicatorData.entry_price}</span></div>
            <div className="bg-zinc-800 p-2 rounded"><span className="text-zinc-500 block">SL</span><span className="font-mono text-red-400">{indicatorData.stop_loss}</span></div>
            <div className="bg-zinc-800 p-2 rounded"><span className="text-zinc-500 block">TP</span><span className="font-mono text-green-400">{indicatorData.take_profit}</span></div>
          </div>
          {isSR && indicatorData.nearest_support && (
            <div className="mt-2 text-xs">
              <span className="text-zinc-400">Support: </span><span className="font-mono text-green-400">{indicatorData.nearest_support}</span>
              <span className="text-zinc-400 ml-3">Resistance: </span><span className="font-mono text-red-400">{indicatorData.nearest_resistance}</span>
            </div>
          )}
          {indicatorData.risk_warning && (
            <p className="text-xs text-orange-400 mt-2 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{lang === "ar" ? indicatorData.risk_warning_ar : indicatorData.risk_warning}</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
